import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  LOCALE_META,
  LOCALE_STORAGE_KEY,
  type AppLocale,
  normalizeLocale,
} from './constants.js';
import { defaultNS, resources } from './resources.js';

let initPromise: Promise<typeof i18n> | null = null;

function readStoredLocale(): AppLocale | null {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    return raw ? normalizeLocale(raw) : null;
  } catch {
    return null;
  }
}

export function resolveInitialLocale(): AppLocale {
  // WHY: clean startup falls back to DEFAULT_LOCALE (ru), not
  // navigator.language. i18n initializes before route context exists,
  // so the global fallback is the only way to guarantee `/login`
  // renders in Russian for first-time users.
  return readStoredLocale() ?? DEFAULT_LOCALE;
}

export function applyDocumentLocale(locale: AppLocale): void {
  if (typeof document === 'undefined') return;
  const meta = LOCALE_META[locale];
  document.documentElement.lang = meta.htmlLang;
  document.documentElement.dir = meta.dir;
}

function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Non-fatal: private mode or quota can block persistence, but the in-memory locale still works.
  }
}

export function initI18n(locale: AppLocale = resolveInitialLocale()): Promise<typeof i18n> {
  if (!initPromise) {
    initPromise = i18n
      .use(initReactI18next)
      .init({
        resources,
        lng: locale,
        fallbackLng: DEFAULT_LOCALE,
        defaultNS,
        ns: Object.keys(resources.ru),
        interpolation: { escapeValue: false },
        returnNull: false,
        react: { useSuspense: false },
      })
      .then(() => i18n);
    i18n.on('languageChanged', (lng) => {
      const nextLocale = normalizeLocale(lng);
      applyDocumentLocale(nextLocale);
      persistLocale(nextLocale);
    });
  } else if (normalizeLocale(i18n.language) !== locale) {
    void i18n.changeLanguage(locale);
  }
  applyDocumentLocale(locale);
  persistLocale(locale);
  return initPromise!;
}

export async function setAppLocale(locale: AppLocale): Promise<void> {
  await initI18n(locale);
  await i18n.changeLanguage(locale);
}

export { i18n };
